import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { useTranslation } from "@/hooks/useTranslation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Pencil, Plus, Warehouse, X } from "lucide-react";
import type { Ulozisko } from "../types";

interface UloziskoManagerProps {
  klientId: number;
  uloziska: Ulozisko[];
  onChanged: () => void;
}

export function UloziskoManager({
  klientId,
  uloziska,
  onChanged,
}: UloziskoManagerProps) {
  const { t } = useTranslation();
  const [newName, setNewName] = useState("");
  const [editId, setEditId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const createMutation = trpc.spajza.createUlozisko.useMutation();
  const updateMutation = trpc.spajza.updateUlozisko.useMutation();
  const deleteMutation = trpc.spajza.deleteUlozisko.useMutation();

  const handleCreate = async () => {
    if (!newName.trim()) return;
    await createMutation.mutateAsync({ klientId, nazov: newName.trim() });
    setNewName("");
    onChanged();
    toast.success(t("mealLibrary:spajza.storageAdded"));
  };

  const handleUpdate = async (id: number) => {
    if (!editName.trim()) return;
    await updateMutation.mutateAsync({ id, klientId, nazov: editName.trim() });
    setEditId(null);
    onChanged();
    toast.success(t("mealLibrary:spajza.storageUpdated"));
  };

  const handleDelete = async (id: number) => {
    await deleteMutation.mutateAsync({ id, klientId });
    setDeleteId(null);
    onChanged();
    toast.success(t("mealLibrary:spajza.storageDeleted"));
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {uloziska.map(u => (
          <div
            key={u.id}
            className="flex items-center gap-1 rounded-md border px-2 py-1 text-sm"
          >
            {editId === u.id ? (
              <>
                <Input
                  className="h-6 w-28 text-xs"
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter") handleUpdate(u.id);
                  }}
                  autoFocus
                />
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-6 w-6"
                  onClick={() => handleUpdate(u.id)}
                >
                  <Plus className="h-3 w-3" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-6 w-6"
                  onClick={() => setEditId(null)}
                >
                  <X className="h-3 w-3" />
                </Button>
              </>
            ) : (
              <>
                <Warehouse className="h-3 w-3 text-muted-foreground" />
                <span>{u.nazov}</span>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-5 w-5 ml-1"
                  onClick={() => {
                    setEditId(u.id);
                    setEditName(u.nazov);
                  }}
                >
                  <Pencil className="h-3 w-3" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-5 w-5 text-destructive hover:text-destructive"
                  onClick={() => setDeleteId(u.id)}
                >
                  <X className="h-3 w-3" />
                </Button>
              </>
            )}
          </div>
        ))}
        <div className="flex items-center gap-1">
          <Input
            className="h-8 w-32 text-sm"
            placeholder={t("mealLibrary:spajza.newStoragePlaceholder")}
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") handleCreate();
            }}
          />
          <Button
            size="sm"
            variant="outline"
            onClick={handleCreate}
            disabled={!newName.trim()}
          >
            <Plus className="h-3 w-3" />
          </Button>
        </div>
      </div>
      <AlertDialog
        open={deleteId !== null}
        onOpenChange={o => !o && setDeleteId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("mealLibrary:spajza.deleteStorage")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("mealLibrary:spajza.deleteStorageConfirmDesc")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common:cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteId && handleDelete(deleteId)}
            >
              {t("mealLibrary:dialog.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
